/* Sentai interactions. Figma prototype timings: docs/MOTION.md. No automatic audio/video. */
(() => {
  'use strict';
  if (window.__sentaiThemeInitialized) return;
  window.__sentaiThemeInitialized = true;
  document.documentElement.classList.replace('no-js', 'js');
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionPreference.matches;
  const APP_DISSOLVE_MS = 300;
  const SPEAKER_DISSOLVE_MS = 1000;
  const SPEAKER_HOLD_MS = 2000;
  const SPEAKER_EXIT_MS = 3000;

  // One state owner prevents overlapping fades after rapid input or editor remounts.
  const dissolvePanels = (panels, signal, area) => {
    // Hidden lazy images otherwise start downloading only after their fade begins, so all
    // the panels' images are fetched once their component (area) comes within about a
    // screen of view. Fetching them as the page opened had them crowd out its first pictures.
    // A live app screen's screenshot is only for browsers that can't show the live one, so it
    // keeps its lazy loading and is fetched only where it's displayed.
    const fetchAll = () => panels.forEach(panel => panel.querySelectorAll('img:not(.st-app-screen__still)').forEach(image => { image.loading = 'eager'; }));
    if ('IntersectionObserver' in window) {
      const watcher = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        watcher.disconnect();
        fetchAll();
      }, { rootMargin: '100% 0px' });
      watcher.observe(area);
      signal?.addEventListener('abort', () => watcher.disconnect(), { once: true });
    } else fetchAll();
    let current = -1;
    let animations = [];
    let revision = 0;
    const cancel = () => {
      revision++;
      animations.forEach(animation => animation.cancel());
      animations = [];
    };
    const show = (index, duration = 0) => {
      const previous = current;
      cancel();
      current = index;
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.setAttribute('aria-hidden', String(i !== index));
      });
      if (previous < 0 || previous === index || !duration || reducedMotion() || !panels[index]?.animate) return;
      const outgoing = panels[previous];
      const incoming = panels[index];
      const version = revision;
      outgoing.hidden = false;
      const options = { duration, easing: 'ease-out', fill: 'both' };
      animations = [
        outgoing.animate([{ opacity: 1 }, { opacity: 0 }], options),
        incoming.animate([{ opacity: 0 }, { opacity: 1 }], options)
      ];
      Promise.all(animations.map(animation => animation.finished)).then(() => {
        if (version !== revision) return;
        outgoing.hidden = true;
        cancel();
      }).catch(() => { /* A newer selection owns visibility after cancellation. */ });
    };
    return { show, cancel, settle: () => show(current, 0) };
  };

  class SpeakerDemo extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const button = this.querySelector('[data-st-speaker-play]');
      const panels = [...this.querySelectorAll('[data-st-speaker-state]')];
      if (!button || panels.length !== 4) return;
      this.transition = dissolvePanels(panels, signal, this);
      this.state = 0;
      this.transition.show(0);
      this.dataset.state = '0';
      const setState = (state, duration) => {
        this.state = state;
        this.dataset.state = String(state);
        this.transition.show(state, duration);
        this.textMotion?.cancel();
        const prompt = panels[state].querySelector('.st-speaker__prompt');
        if (!duration || reducedMotion() || !prompt?.animate) return;
        // Questions (even states) drop in from above; Sentai's answers rise from below.
        const offset = state % 2 ? 40 : -40;
        this.textMotion = prompt.animate(
          [{ transform: `translateY(${offset}px)` }, { transform: 'none' }],
          { duration: Math.min(duration, SPEAKER_DISSOLVE_MS), easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      };
      const reset = () => {
        clearTimeout(this.timer);
        setState(0, this.state === 3 ? SPEAKER_EXIT_MS : 0);
      };
      const advance = () => {
        setState(this.state + 1, SPEAKER_DISSOLVE_MS);
        if (this.state < 3) this.timer = setTimeout(advance, SPEAKER_DISSOLVE_MS + SPEAKER_HOLD_MS);
      };
      const start = () => {
        clearTimeout(this.timer);
        // Reduce-motion users can read each state at their own pace.
        if (reducedMotion()) {
          setState((this.state + 1) % panels.length, 0);
          return;
        }
        setState(0, 0);
        advance();
      };
      button.addEventListener('click', start, { signal });
      button.addEventListener('keydown', event => {
        if (event.key === 'Escape') reset();
      }, { signal });
      motionPreference.addEventListener('change', () => {
        clearTimeout(this.timer);
        this.transition.settle();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { clearTimeout(this.timer); setState(0, 0); }
      }, { signal });
      if (window.IntersectionObserver) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting) { clearTimeout(this.timer); setState(0, 0); this.autoPlayed = false; }
        });
        this.observer.observe(this);
        // Scrolling plays the sequence once each time the speaker's top is 70% up the viewport.
        this.autoPlayObserver = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting || this.autoPlayed || reducedMotion()) return;
          this.autoPlayed = true;
          start();
        }, { rootMargin: '0px 0px -70% 0px' });
        this.autoPlayObserver.observe(this);
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      this.transition?.cancel();
      this.textMotion?.cancel();
      this.observer?.disconnect();
      this.autoPlayObserver?.disconnect();
    }
  }

  class AppTabs extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const tabs = [...this.querySelectorAll('[data-st-tab]')];
      const panels = [...this.querySelectorAll('[data-st-panel]')];
      const dots = [...this.querySelectorAll('[data-st-dot]')];
      const dotControls = this.querySelector('[data-st-dots]');
      const list = this.querySelector('[data-st-tabs]');
      if (!list || !tabs.length) return;
      this.transition = dissolvePanels(panels, signal, this);
      motionPreference.addEventListener('change', () => {
        this.transition.settle();
        this.notificationMotion?.cancel();
      }, { signal });
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-orientation', list.dataset.stOrientation || 'vertical');
      let current = 0;
      // source: 'user' for a visitor's choice, 'auto' when app reels move on by themselves.
      const select = (index, focus = false, source = 'user') => {
        current = index;
        this.notificationMotion?.cancel();
        tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.disabled = false;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', String(selected));
          tab.setAttribute('aria-controls', tab.dataset.panel);
          tab.tabIndex = selected ? 0 : -1;
          const panel = panels.find(p => p.id === tab.dataset.panel);
          if (panel) {
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', tab.id);
          }
        });
        const panelIndex = panels.findIndex(panel => panel.id === tabs[index].dataset.panel);
        if (panelIndex >= 0) this.transition.show(panelIndex, APP_DISSOLVE_MS);
        const notification = panels[panelIndex]?.querySelector('[data-st-notification]');
        if (notification?.animate && !reducedMotion()) {
          this.notificationMotion = notification.animate([{ opacity: 0 }, { opacity: 1 }], {
            delay: 800, duration: APP_DISSOLVE_MS, easing: 'ease-out', fill: 'backwards'
          });
        }
        dots.forEach((dot, i) => {
          dot.disabled = false;
          dot.setAttribute('aria-pressed', String(i === index));
          dot.tabIndex = i === index ? 0 : -1;
        });
        if (dotControls) dotControls.dataset.active = String(index);
        if (focus) tabs[index].focus();
        if (panelIndex >= 0) this.reels?.select(panelIndex, source);
      };
      // App reels (st-app-reels.js) and dashboard reels (st-dash.js), loaded by the sections that
      // use them, advance through this.
      this.selectFeature = index => select(index, false, 'auto');
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index), { signal });
        tab.addEventListener('keydown', event => {
          let next = index;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault();
          select(next, true);
        }, { signal });
      });
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => select(index), { signal });
        dot.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault();
          select(next);
          dots[next].focus();
        }, { signal });
      });
      if (dotControls) {
        dotControls.hidden = false;
        const visual = this.querySelector('.st-app-visual');
        let pointer;
        visual.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0 || event.target.closest('button')) return;
          pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
          visual.setPointerCapture(event.pointerId);
        }, { signal });
        visual.addEventListener('pointerup', event => {
          if (!pointer || pointer.id !== event.pointerId) return;
          const dx = event.clientX - pointer.x;
          const dy = event.clientY - pointer.y;
          pointer = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            select((current + (dx < 0 ? 1 : -1) + tabs.length) % tabs.length);
          }
        }, { signal });
        visual.addEventListener('pointercancel', () => { pointer = null; }, { signal });
        // Firefox ignores -webkit-user-drag, so a mouse swipe would pick the screenshot up as a
        // file to drag and cancel the swipe.
        visual.addEventListener('dragstart', event => event.preventDefault(), { signal });
      }
      this.addEventListener('shopify:block:select', event => {
        const index = tabs.findIndex(tab => tab.dataset.stBlockId === event.detail?.blockId);
        if (index >= 0) select(index);
      }, { signal });
      select(0, false, 'init');
      window.SentaiAppReels?.attach(this);
      window.SentaiDashReels?.attach(this);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.transition?.cancel();
      this.notificationMotion?.cancel();
      this.reels?.destroy();
      this.reels = null;
    }
  }

  class TestimonialReel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const reel = this.querySelector('[data-st-reel]');
      const previous = this.querySelector('[data-st-reel-prev]');
      const next = this.querySelector('[data-st-reel-next]');
      const controls = this.querySelector('[data-st-reel-controls]');
      const status = this.querySelector('[data-st-reel-status]');
      if (!reel || !previous || !next || !controls) return;
      const cards = [...reel.children];
      // The status follows the reel itself, so scrolling it with the keyboard or a finger
      // is announced as well as pressing the arrows. Announced once the scroll settles.
      let announced = 0, primed = false, settle;
      const announce = () => {
        if (!status || !cards.length) return;
        const gap = parseFloat(getComputedStyle(reel).gap) || 0;
        const step = (cards[0]?.getBoundingClientRect().width || reel.clientWidth) + gap;
        const index = Math.max(1, Math.min(cards.length, Math.round(Math.abs(reel.scrollLeft) / step) + 1));
        if (index === announced) return;
        announced = index;
        // The resting position is where the reader starts; announce only once it moves.
        if (!primed) { primed = true; return; }
        status.textContent = `Testimonial ${index} of ${cards.length}`;
      };
      const update = () => {
        const position = Math.abs(reel.scrollLeft);
        const end = Math.max(0, reel.scrollWidth - reel.clientWidth);
        previous.disabled = position <= 2;
        next.disabled = position >= end - 2;
        controls.hidden = end <= 2;
        clearTimeout(settle);
        settle = setTimeout(announce, 250);
      };
      const move = direction => {
        const gap = parseFloat(getComputedStyle(reel).gap) || 0;
        const step = (cards[0]?.getBoundingClientRect().width || reel.clientWidth) + gap;
        const rtl = getComputedStyle(reel).direction === 'rtl' ? -1 : 1;
        reel.scrollBy({ left: direction * step * rtl, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      reel.addEventListener('scroll', update, { signal, passive: true });
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(update);
        this.observer.observe(reel);
      }
      update();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
  }

  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const button = this.querySelector('[data-st-video-play]');
      // The file player is already in the page (Safari refuses to start one inserted on click);
      // embeds are still built from their template so nothing third-party loads until asked.
      const target = this.querySelector('[data-st-video-target]');
      const template = this.querySelector('[data-st-video-template]');
      if (!button || !(target || template)) return;
      button.disabled = false;
      button.addEventListener('click', () => {
        if (target) {
          const video = target.querySelector('video');
          target.classList.remove('st-video-idle');
          button.hidden = true;
          if (video) {
            const play = video.play();
            video.focus({ preventScroll: true });
            if (play && typeof play.catch === 'function') play.catch(() => { video.controls = true; });
          }
          return;
        }
        this.replaceChildren(document.importNode(template.content, true));
        this.querySelector('iframe')?.focus();
      }, { signal: this.abort.signal, once: true });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.querySelector('video')?.pause();
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const source = this.querySelector('[data-st-product-data]');
      const variantChoice = this.querySelector('[data-st-variant-choice]');
      const variantInput = this.querySelector('[data-st-variant-id]');
      const planChoice = this.querySelector('[data-st-plan-choice]');
      const planField = this.querySelector('[data-st-plan-field]');
      const price = this.querySelector('[data-st-product-price]');
      const compare = this.querySelector('[data-st-product-compare]');
      const button = this.querySelector('[data-st-add-to-cart]');
      const quantity = this.querySelector('[data-st-quantity]');
      const status = this.querySelector('[data-st-product-status]');
      if (!source || !variantChoice || !variantInput || !button) return;
      let data;
      try { data = JSON.parse(source.textContent); }
      catch (error) { console.warn('Sentai: product data could not be read; use Update selection.', error); return; }
      this.querySelector('[data-st-update-variant]')?.setAttribute('hidden', '');
      const refreshForm = this.querySelector('[data-st-variant-form]');
      refreshForm?.addEventListener('submit', event => event.preventDefault(), { signal });
      let previousVariant = null;
      const update = () => {
        const variant = data.variants.find(v => String(v.id) === variantChoice.value);
        if (!variant) {
          button.disabled = true;
          button.textContent = data.labels.unavailable;
          return;
        }
        variantInput.value = String(variant.id);
        const requiresPlan = Boolean(data.requiresPlan || variant.requiresPlan);
        if (planChoice && String(previousVariant) !== String(variant.id)) {
          const requestedPlan = planChoice.value;
          const options = [];
          if (!requiresPlan) options.push(new Option(data.labels.oneTime, ''));
          else {
            const placeholder = new Option(data.labels.choosePlan, '');
            placeholder.disabled = true;
            options.push(placeholder);
          }
          variant.plans.forEach(plan => options.push(new Option(`${plan.name} - ${plan.price}`, String(plan.id))));
          planChoice.replaceChildren(...options);
          planChoice.required = Boolean(requiresPlan);
          planChoice.value = variant.plans.some(p => String(p.id) === requestedPlan) ? requestedPlan : '';
          if (planField) planField.hidden = !requiresPlan && variant.plans.length === 0;
          previousVariant = variant.id;
        }
        const plan = variant.plans.find(p => String(p.id) === planChoice?.value);
        if (price) price.textContent = plan ? plan.price : variant.price;
        if (compare) {
          compare.textContent = plan ? plan.compare : variant.compare;
          compare.hidden = plan ? !plan.onSale : !variant.onSale;
        }
        if (quantity) {
          quantity.min = String(variant.minimum || 1);
          quantity.step = String(variant.increment || 1);
          if (variant.maximum) quantity.max = String(variant.maximum); else quantity.removeAttribute('max');
          if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
          if (variant.maximum && Number(quantity.value) > variant.maximum) quantity.value = String(variant.maximum);
        }
        const missingPlan = requiresPlan && !plan;
        button.disabled = !variant.available || missingPlan;
        button.textContent = !variant.available ? data.labels.soldOut : missingPlan ? data.labels.choosePlan : data.labels.add;
        if (status) status.textContent = !variant.available ? data.labels.soldOut : missingPlan ? data.labels.choosePlan : '';
        if (!window.Shopify?.designMode) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          if (plan) url.searchParams.set('selling_plan', String(plan.id)); else url.searchParams.delete('selling_plan');
          window.history.replaceState({}, '', url);
        }
      };
      variantChoice.addEventListener('change', update, { signal });
      planChoice?.addEventListener('change', update, { signal });
      update();
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  // A small overscan keeps the frame covered while the photograph moves slowly.
  class GentleParallax extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      if (!this.hasAttribute('data-st-parallax')) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.active = false;
      const update = () => {
        this.frame = 0;
        if (!this.active || document.hidden || reducedMotion()) return;
        const bounds = this.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1,
          (innerHeight / 2 - bounds.top - bounds.height / 2) / (innerHeight / 2 + bounds.height / 2)));
        const offset = progress * Math.min(24, bounds.height * 0.035);
        this.style.setProperty('--st-parallax-y', `${offset.toFixed(2)}px`);
      };
      const schedule = () => {
        if (this.active && !document.hidden && !reducedMotion() && !this.frame) this.frame = requestAnimationFrame(update);
      };
      const configure = () => {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.observer?.disconnect();
        this.active = false;
        this.removeAttribute('data-st-parallax-ready');
        this.style.removeProperty('--st-parallax-y');
        if (reducedMotion() || !window.IntersectionObserver) return;
        this.setAttribute('data-st-parallax-ready', '');
        this.observer = new IntersectionObserver(entries => {
          this.active = entries[0].isIntersecting;
          if (this.active) schedule();
        }, { rootMargin: '100px' });
        this.observer.observe(this);
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      document.addEventListener('visibilitychange', schedule, { signal });
      motionPreference.addEventListener('change', configure, { signal });
      configure();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.removeAttribute('data-st-parallax-ready');
      this.style.removeProperty('--st-parallax-y');
    }
  }

  class ScenarioSamples extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.querySelectorAll('[data-st-audio-sample]').forEach(sample => {
        const audio = sample.querySelector('audio');
        const button = sample.querySelector('[data-st-audio-toggle]');
        const action = sample.querySelector('[data-st-audio-action]');
        const status = sample.querySelector('[data-st-audio-status]');
        if (!audio || !button) return;
        const sync = () => {
          const playing = !audio.paused && !audio.ended;
          button.setAttribute('aria-pressed', String(playing));
          button.setAttribute('aria-label', (playing ? 'Stop ' : 'Play ') + button.dataset.title + ' sample');
          action.textContent = playing ? 'Stop' : 'Play';
        };
        const failed = () => {
          status.textContent = 'This sample could not be played. Please try again.';
          audio.controls = true;
          sync();
        };
        button.hidden = false;
        audio.controls = false;
        sync();
        button.addEventListener('click', () => {
          status.textContent = '';
          if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
            return;
          }
          document.querySelectorAll('st-scenarios audio').forEach(other => { if (other !== audio) other.pause(); });
          audio.play()?.catch(error => { if (error.name !== 'AbortError') failed(); });
        }, { signal });
        audio.addEventListener('play', sync, { signal });
        audio.addEventListener('pause', sync, { signal });
        audio.addEventListener('ended', sync, { signal });
        audio.addEventListener('error', failed, { signal });
      });
      const pause = () => this.querySelectorAll('audio').forEach(audio => audio.pause());
      document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); }, { signal });
      window.addEventListener('pagehide', pause, { signal });
    }
    disconnectedCallback() {
      this.querySelectorAll('audio').forEach(audio => audio.pause());
      this.abort?.abort();
    }
  }

  // Press logos. In "Moving strip" layout the logos slide only when they do not fit the width
  // and the visitor has not asked for reduced motion; otherwise they sit in centred rows. The
  // strip opens where the stylesheet drew it (the copy's first logo just past the left fade) and
  // stays still until it is in view. It rests while a mouse is over it, and a click on it or its
  // button stops and starts it. Each logo is animated on its own small layer, all on one clock:
  // Safari tiles a single layer as wide as the two-copy track and can blank it as it starts.
  class PressStrip extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      const track = this.querySelector('[data-st-press-track]');
      const list = track?.querySelector('.st-press__list');
      const copy = track?.querySelector('[data-st-press-copy]');
      const toggle = this.querySelector('[data-st-press-toggle]');
      const label = this.querySelector('[data-st-press-label]');
      if (!track || !list || !copy || !toggle || this.dataset.layout !== 'scroll') return;
      const items = [...list.children];
      const logos = [...track.querySelectorAll('.st-press__item')];
      let moving = false, paused = false, hovering = false, waiting = true, frame = 0;
      let animations = [], loop = 0, duration = 0;
      const apply = () => {
        const run = moving && !paused && !hovering && !waiting;
        animations.forEach(animation => (run ? animation.play() : animation.pause()));
        this.classList.toggle('is-paused', moving && (paused || hovering));
        toggle.setAttribute('aria-pressed', String(paused));
        if (label) label.textContent = paused ? 'Play the moving logos' : 'Pause the moving logos';
      };
      // The distance from a logo to its twin in the copy, in layout pixels (the page zoom scales
      // client rectangles), so the loop closes without a seam.
      const measureLoop = () => {
        const scale = parseFloat(getComputedStyle(track).width) / track.getBoundingClientRect().width || 1;
        return (copy.firstElementChild.getBoundingClientRect().left - list.firstElementChild.getBoundingClientRect().left) * scale;
      };
      const animate = progress => {
        animations.forEach(animation => animation.cancel());
        duration = loop / 36 * 1000;
        animations = logos.map(logo => logo.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-loop}px)` }], { duration, iterations: Infinity }));
        animations.forEach(animation => { animation.currentTime = progress * duration; });
        apply();
      };
      const measure = () => {
        frame = 0;
        // Layout pixels on both sides, so the comparison holds under the page zoom.
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        const row = items.reduce((sum, item) => sum + item.offsetWidth, 0) + gap * Math.max(0, items.length - 1);
        // Rows sit inside the page gutters; the moving strip runs to the page's edges. Measuring
        // against the gutters either way keeps the choice from flipping at the boundary.
        const gutter = parseFloat(getComputedStyle(this).getPropertyValue('--st-gutter')) || 0;
        const width = this.querySelector('.st-press__window').clientWidth;
        const room = moving ? width - gutter * 2 : width;
        const move = !reducedMotion() && row > room;
        if (move && moving) {
          // A new breakpoint changes the logo sizes: keep the strip's place in its loop.
          const next = measureLoop();
          if (Math.abs(next - loop) > 0.5) {
            const progress = animations[0] ? (animations[0].currentTime % duration) / duration : 0;
            loop = next;
            animate(progress);
          }
        }
        if (move === moving) { this.classList.add('is-ready'); toggle.hidden = !moving; return; }
        moving = move;
        this.classList.toggle('is-moving', moving);
        this.classList.add('is-ready');
        toggle.hidden = !moving;
        if (moving) {
          // Start where the stylesheet drew the strip: the copy's first logo just past the 8% fade.
          loop = measureLoop();
          animate((loop - Math.min(loop, width * 0.08 + 8)) / loop);
        } else {
          animations.forEach(animation => animation.cancel());
          animations = [];
          apply();
        }
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
      const flip = () => { if (!moving) return; paused = !paused; apply(); };
      toggle.addEventListener('click', event => { event.stopPropagation(); flip(); }, { signal });
      this.querySelector('.st-press__window').addEventListener('click', flip, { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovering = true; apply(); } }, { signal });
      this.addEventListener('pointerleave', () => { hovering = false; apply(); }, { signal });
      motionPreference.addEventListener('change', schedule, { signal });
      if (window.IntersectionObserver) {
        this.viewer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          waiting = false;
          this.viewer.disconnect();
          apply();
        }, { threshold: 0.6 });
        this.viewer.observe(this);
      } else waiting = false;
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(schedule);
        this.observer.observe(this);
      }
      measure();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.viewer?.disconnect(); }
  }

  for (const [name, element] of [
    ['st-app-tabs', AppTabs], ['st-reel', TestimonialReel],
    ['st-video', DeferredVideo], ['st-product', ProductForm], ['st-speaker-demo', SpeakerDemo],
    ['st-parallax', GentleParallax], ['st-scenarios', ScenarioSamples], ['st-press', PressStrip]
  ]) if (!customElements.get(name)) customElements.define(name, element);

  // Liquid selects the displayed page template. Pricing now leaves for the shop rather
  // than jumping to a fragment, so the marker only tracks the page.
  const syncNavigation = () => {
    document.querySelectorAll('.st-nav-list a').forEach(link => {
      if (link.hasAttribute('data-st-page-current')) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  };
  syncNavigation();
  window.addEventListener('hashchange', syncNavigation);
  document.addEventListener('shopify:section:load', syncNavigation);

  // Reveal on scroll arrival, including the mobile overlays below each photo.
  const revealTargets = '[data-st-statement], [data-st-reveal], [data-st-bounce], [data-st-notify]';
  const preparedReveals = new WeakSet();
  const observeReveals = options => window.IntersectionObserver ? new IntersectionObserver(entries => {
    // Elements that arrive together, such as a row of cards, are revealed one after another.
    entries.filter(entry => entry.isIntersecting).forEach((entry, order) => reveal(entry.target, order));
  }, options) : null;
  // Phones are tall, so an element can sit blank for a long scroll before it reaches its trigger
  // line; on those screens every trigger starts much sooner.
  const compactViewport = window.matchMedia('(max-width: 767px), (max-width: 1024px) and (orientation: portrait)');
  const line = (wide, compact) => `0px 0px ${compactViewport.matches ? compact : wide}% 0px`;
  let statementObserver, bounceObserver, notifyObserver, chatObserver, revealObserver;
  // Some notifications follow another element: data-st-notify-at names what to watch.
  const anchoredNotifications = new WeakMap();
  const observeAnchors = (options, ready) => window.IntersectionObserver ? new IntersectionObserver(entries => {
    entries.filter(ready).forEach((entry, order) => reveal(anchoredNotifications.get(entry.target), order));
  }, options) : null;
  const anchors = {
    // As soon as the photo's bottom edge is on screen; the extended top margin leaves only that edge
    // deciding. Safari never reports a ratio of exactly 1 for a box the page zoom leaves on a
    // fraction, so watch a spread of thresholds and test the edge itself. The test is monotonic:
    // once the bottom is on screen it stays true, so a fast flick cannot scroll past it.
    'photo-bottom': {
      observer: observeAnchors({ threshold: [0, .25, .5, .75, .9, .99, 1], rootMargin: '100000px 0px 0px 0px' },
        // rootBounds is null in some Safari builds and inside a cross-origin frame, such as the
        // theme editor preview, so fall back to the viewport rather than staying hidden.
        entry => entry.isIntersecting
          && entry.boundingClientRect.bottom <= (entry.rootBounds ? entry.rootBounds.bottom
            : (window.innerHeight || document.documentElement.clientHeight)) + 1),
      find: element => element.parentElement?.querySelector(':scope > img')
    },
    // As soon as the step card's top edge reaches the trigger line.
    'card-middle': {
      observer: null,
      find: element => element.closest('.st-step')
    }
  };
  const buildObservers = () => {
    // Statements wait until half their text has passed the trigger line.
    statementObserver = observeReveals({ threshold: 0.5, rootMargin: line(-20, -6) });
    // Bouncing panels start once their top edge passes it.
    bounceObserver = observeReveals({ threshold: 0, rootMargin: line(-20, -6) });
    // Notifications wait until they are well up the viewport.
    notifyObserver = observeReveals({ threshold: 0, rootMargin: line(-70, -40) });
    // Chat messages arrive earlier.
    chatObserver = observeReveals({ threshold: 0, rootMargin: line(-50, -25) });
    revealObserver = observeReveals({ threshold: 0.15, rootMargin: line(-8, -2) });
    anchors['card-middle'].observer = observeAnchors({ threshold: 0, rootMargin: line(-50, -35) }, entry => entry.isIntersecting);
  };
  buildObservers();
  const anchorFor = element => {
    const anchor = anchors[element.dataset.stNotifyAt];
    const target = anchor?.observer && anchor.find(element);
    return target ? { observer: anchor.observer, target } : null;
  };
  const observerFor = element => element.matches('[data-st-statement]') ? statementObserver
    : element.matches('[data-st-bounce]') ? bounceObserver
    : element.matches('[data-st-notify-stagger]') ? chatObserver
    : element.matches('[data-st-notify]') ? notifyObserver : revealObserver;
  const reveal = (element, order = 0) => {
    if (element.matches('[data-st-bounce], [data-st-notify]')) {
      const gap = Number(element.dataset.stGap) || (element.matches('[data-st-notify]') ? 250 : 150);
      element.style.setProperty('--st-order-delay', `${order * gap}ms`);
    }
    element.removeAttribute('data-st-pending');
    unwatch(element);
  };
  const watch = element => {
    const anchor = anchorFor(element);
    if (anchor) {
      anchoredNotifications.set(anchor.target, element);
      anchor.observer.observe(anchor.target);
    } else {
      observerFor(element).observe(element);
    }
  };
  const unwatch = element => {
    const anchor = anchorFor(element);
    if (anchor) anchor.observer.unobserve(anchor.target);
    else observerFor(element)?.unobserve(element);
  };
  // Anything already on screen when the page loads stays visible: it would otherwise sit blank
  // until the visitor scrolls far enough to cross its trigger line.
  let firstPaint = true;
  const onScreenAtLoad = element => {
    const bounds = element.getBoundingClientRect();
    return bounds.top < window.innerHeight && bounds.bottom > 0;
  };
  const prepareReveals = (root = document) => {
    root.querySelectorAll(revealTargets).forEach(element => {
      if (preparedReveals.has(element)) return;
      preparedReveals.add(element);
      if (reducedMotion() || !observerFor(element)) return;
      if (firstPaint && onScreenAtLoad(element)) return;
      element.setAttribute('data-st-pending', '');
      watch(element);
    });
  };
  // Safari has not been dependable about delivering this one trigger, so a passive scroll check
  // resolves the same condition: the photo's bottom edge on screen. It costs one attribute query
  // per frame of scrolling and unhooks itself as soon as nothing is waiting on it.
  const anchoredPending = () => document.querySelectorAll('[data-st-notify-at="photo-bottom"][data-st-pending]');
  let anchorFrame = 0;
  const checkAnchoredNotifications = () => {
    anchorFrame = 0;
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    anchoredPending().forEach(element => {
      const photo = anchors['photo-bottom'].find(element);
      if (photo && photo.getBoundingClientRect().bottom <= viewport + 1) reveal(element);
    });
    if (!anchoredPending().length) window.removeEventListener('scroll', queueAnchorCheck);
  };
  const queueAnchorCheck = () => { anchorFrame = anchorFrame || requestAnimationFrame(checkAnchoredNotifications); };
  const watchAnchorsOnScroll = () => {
    if (anchoredPending().length) window.addEventListener('scroll', queueAnchorCheck, { passive: true });
  };

  prepareReveals();
  watchAnchorsOnScroll();
  requestAnimationFrame(() => { firstPaint = false; });
  document.addEventListener('shopify:section:load', event => { prepareReveals(event.target); watchAnchorsOnScroll(); });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(revealTargets).forEach(element => {
      unwatch(element);
      preparedReveals.delete(element);
      element.removeAttribute('data-st-pending');
    });
  });
  compactViewport.addEventListener('change', () => {
    const pending = [...document.querySelectorAll('[data-st-pending]')];
    pending.forEach(unwatch);
    [statementObserver, bounceObserver, notifyObserver, chatObserver, revealObserver, anchors['card-middle'].observer]
      .forEach(observer => observer?.disconnect());
    buildObservers();
    pending.forEach(watch);
  });
  motionPreference.addEventListener('change', () => {
    if (reducedMotion()) document.querySelectorAll('[data-st-pending]').forEach(element => reveal(element));
  });

  // The hub's rings ripple out once, as the hub's centre passes a line low in the viewport, so
  // the visitor sees them leave it. A passive scroll check, like the photo-bottom one above,
  // since the rings' box is far taller than the part that matters; it unhooks once all have played.
  const ringsWaiting = () => document.querySelectorAll('[data-st-rings="waiting"]');
  const ringsCentre = rings => {
    const bounds = rings.getBoundingClientRect();
    return bounds.top + bounds.height * 434.256 / 841;
  };
  let ringsFrame = 0;
  const checkRings = () => {
    ringsFrame = 0;
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const lineAt = viewport * (compactViewport.matches ? .8 : .7);
    ringsWaiting().forEach(rings => {
      if (!('stLanding' in rings.dataset) && ringsCentre(rings) <= lineAt) rings.dataset.stRings = 'play';
    });
    if (!ringsWaiting().length) window.removeEventListener('scroll', queueRingsCheck);
  };
  const queueRingsCheck = () => { ringsFrame = ringsFrame || requestAnimationFrame(checkRings); };
  const prepareRings = (root = document) => {
    root.querySelectorAll('[data-st-rings=""]').forEach(rings => {
      // Rings already in view when the page loads stay drawn, as other reveals do.
      const centre = ringsCentre(rings);
      if (reducedMotion() || (firstPaint && centre < window.innerHeight && centre > 0)) {
        rings.dataset.stRings = 'shown';
        return;
      }
      rings.dataset.stRings = 'waiting';
      // A visitor who jumps to the section reaches both lines at once, and the ripple would be
      // half spent while the hub's photo is still rising in; wait for it to land. Landing also
      // re-checks, for a photo that settles past the line once scrolling has stopped.
      const photo = rings.closest('[data-st-bounce]');
      if (!photo?.hasAttribute('data-st-pending')) return;
      rings.dataset.stLanding = '';
      const landed = event => {
        if (event.target !== photo || event.propertyName !== 'transform') return;
        delete rings.dataset.stLanding;
        ['transitionend', 'transitioncancel'].forEach(type => photo.removeEventListener(type, landed));
        queueRingsCheck();
      };
      ['transitionend', 'transitioncancel'].forEach(type => photo.addEventListener(type, landed));
    });
    if (ringsWaiting().length) {
      window.addEventListener('scroll', queueRingsCheck, { passive: true });
      queueRingsCheck();
    }
  };
  prepareRings();
  document.addEventListener('shopify:section:load', event => prepareRings(event.target));
  motionPreference.addEventListener('change', () => {
    if (reducedMotion()) ringsWaiting().forEach(rings => { rings.dataset.stRings = 'shown'; });
  });

  const prepareDialog = () => {
    document.querySelectorAll('[data-st-open-video]').forEach(button => { button.disabled = false; });
    const dialog = document.getElementById('st-overview-dialog');
    if (!dialog || dialog.dataset.stReady) return;
    dialog.dataset.stReady = 'true';
    dialog.addEventListener('close', () => {
      const content = dialog.querySelector('[data-st-dialog-content]');
      content?.querySelector('video')?.pause();
      content?.replaceChildren();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
  };
  prepareDialog();
  document.addEventListener('shopify:section:load', prepareDialog);
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-st-open-video]')) {
      const dialog = document.getElementById('st-overview-dialog');
      const template = dialog?.querySelector('[data-st-dialog-template]');
      const content = dialog?.querySelector('[data-st-dialog-content]');
      if (dialog && template && content && !dialog.open) {
        content.replaceChildren(template.content.cloneNode(true));
        dialog.showModal();
        const video = content.querySelector('video');
        video?.play()?.catch(() => {});
      }
    }
    if (target.closest('[data-st-close-video]')) document.getElementById('st-overview-dialog')?.close();
    // The eGuide form opens inside its own panel. HubSpot's embed is fetched the first
    // time it is asked for, so a reader who never wants the guide never loads a
    // third-party script.
    const eguideOpen = target.closest('[data-st-eguide-open]');
    if (eguideOpen) {
      const drawer = document.getElementById(eguideOpen.getAttribute('data-st-eguide-open'));
      if (drawer) {
        const opening = drawer.hidden;
        if (opening) {
          const mount = drawer.querySelector('[data-st-eguide-form]');
          const portal = drawer.dataset.portal, form = drawer.dataset.form;
          if (mount && portal && form && !mount.dataset.loaded) {
            mount.dataset.loaded = 'true';
            const frame = document.createElement('div');
            frame.className = 'hs-form-frame';
            frame.dataset.region = drawer.dataset.region || 'na1';
            frame.dataset.formId = form;
            frame.dataset.portalId = portal;
            mount.replaceChildren(frame);
            const script = document.createElement('script');
            script.src = 'https://js.hsforms.net/forms/embed/' + encodeURIComponent(portal) + '.js';
            script.defer = true;
            document.body.appendChild(script);
          }
        }
        drawer.hidden = !opening;
        eguideOpen.setAttribute('aria-expanded', String(opening));
      }
    }
    document.querySelectorAll('[data-st-menu][open]').forEach(menu => {
      if (!menu.contains(target) || target.closest('a')) menu.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-st-menu][open]').forEach(menu => {
      menu.open = false;
      menu.querySelector('summary')?.focus();
    });
  });
})();
